/**
 * Kiosk instruction animation — a single looping SVG demonstrating the
 * two ways to check in: swiping a UMD ID card through the reader on the
 * right side of the tablet, and holding a phone's QR code up to the
 * scanner on the left side.
 *
 * The card reader is split into two z-layers (back + front strip) with
 * the card sandwiched between them, creating the illusion that the
 * card passes through the reader slot. The QR scanner never occludes
 * or is occluded — the phone is held in front of it, not inserted — so
 * it's drawn as a single static module plus a glow that lights up while
 * "reading" the code.
 *
 * Animation (18s loop, two acts):
 *   Act 1 — Card swipe (0–50%, mirrors the original 9s card-only loop):
 *     0–11%    Card front, large, centered on tablet screen
 *     11–19%   Shrink + rotate to portrait + flip to back + slide to reader + zoom in
 *     19–21%   Settle at top of reader
 *     21–29%   Swipe down through reader
 *     29–33%   Hold at bottom
 *     33–38%   Card fades out
 *     38–44%   Zoom out + invisible reset
 *     44–50%   (card stays hidden through Act 2; it reappears at 96–100%)
 *
 *   Act 2 — Phone QR scan (50–100%):
 *     50–53%   Phone fades in, large, centered on tablet screen
 *     53–61%   Hold, centered
 *     61–69%   Shrink + slide to the scanner (left side) + zoom in
 *     69–86%   Hold in front of scanner; scanner glow + a green laser sweeping
 *              up and down over the QR code light up (72–86%)
 *     86–90%   Phone fades out
 *     90–93%   Zoom out + reset to center (phone stays hidden the rest of the loop)
 *     96–100%  Card fades back in at center, ready for Act 1 to repeat
 */
import React, { useMemo } from "react";
import { Global, css } from "@emotion/react";
import { CardFront, CardBack, CARD_W as CW, CARD_H as CH } from "./UmdIdCard";
import { PhoneWithQr, PHONE_W, PHONE_H, QR_X, QR_Y, QR_BOX, QR_QUIET } from "./Phone";
const DUR = "18s";

// Scan-laser sweep bounds — the QR's white quiet-zone border, in the
// phone's own local coordinate space (same space PhoneWithQr draws in).
const QR_LEFT = QR_X - QR_QUIET;
const QR_TOP = QR_Y - QR_QUIET;
const QR_WIDTH = QR_BOX + QR_QUIET * 2;
const QR_HEIGHT = QR_BOX + QR_QUIET * 2;
const LASER_SWEEP = QR_HEIGHT - 4; // leaves room so the beam's glow doesn't overshoot the box

// Card center in native coords (for transform-origin)
const CCX = CW / 2; // 90
const CCY = CH / 2; // 56.5

// Phone center in native coords (for transform-origin)
const PCX = PHONE_W / 2; // 32
const PCY = PHONE_H / 2; // 64

// Scale values
const SCALE_START = 2.0;  // card larger than the tablet
const SCALE_END = 0.35;   // card at swipe size
const PHONE_SCALE_START = 1.6; // phone larger than the tablet, on intro
const PHONE_SCALE_END = 0.55;  // phone held up to the scanner

/* ================================================================== */
/*  Tablet on Pole Stand                                               */
/* ================================================================== */
function TabletOnStand({ x, y, w, h, uid }) {
  const bezel = 8;
  const poleW = Math.round(w * 0.1);

  return (
    <g transform={`translate(${x},${y})`}>
      <defs>
        <linearGradient id={`pole-${uid}`} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#999" />
          <stop offset="30%" stopColor="#ccc" />
          <stop offset="50%" stopColor="#ddd" />
          <stop offset="70%" stopColor="#ccc" />
          <stop offset="100%" stopColor="#999" />
        </linearGradient>
        <linearGradient id={`glare-${uid}`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="white" stopOpacity="0.03" />
          <stop offset="100%" stopColor="white" stopOpacity="0.01" />
        </linearGradient>
      </defs>
      <rect x={(w - poleW) / 2} y={h} width={poleW} height={200} fill={`url(#pole-${uid})`} />
      <rect width={w} height={h} rx={12} fill="#111" />
      <rect x={bezel} y={bezel} width={w - bezel * 2} height={h - bezel * 2} rx={3} fill="#1e1e1e" />
      <rect x={bezel} y={bezel} width={w - bezel * 2} height={h - bezel * 2} rx={3} fill={`url(#glare-${uid})`} />
    </g>
  );
}

/* ================================================================== */
/*  Reader — Back layer (behind card)                                  */
/* ================================================================== */
function ReaderBack({ x, y, h = 130 }) {
  const rW = 18;
  return (
    <g transform={`translate(${x},${y})`}>
      <rect width={rW} height={h} rx={4} fill="#1a1a1a" />
      <rect x={1.5} y={2} width={rW - 3} height={h - 4} rx={2.5} fill="#222" />
      {/* slot groove */}
      <rect x={6} y={h * 0.04} width={2} height={h * 0.92} rx={0.8} fill="#0a0a0a" />
      {/* rounded caps */}
      <ellipse cx={rW / 2} cy={2.5} rx={5} ry={2.5} fill="#1a1a1a" />
      <ellipse cx={rW / 2} cy={h - 2.5} rx={5} ry={2.5} fill="#1a1a1a" />
    </g>
  );
}

/* ================================================================== */
/*  Reader — Front strip (in front of card = occluder)                 */
/* ================================================================== */
function ReaderFront({ x, y, h = 130 }) {
  const stripW = 8;
  return (
    <g transform={`translate(${x},${y})`}>
      {/* Front strip that occludes the card's left edge */}
      <rect width={stripW} height={h} rx={3} fill="#1a1a1a" />
      <rect x={1} y={1.5} width={stripW - 2} height={h - 3} rx={2} fill="#252525" />
      {/* Slot shadow line */}
      <rect x={stripW - 0.8} y={h * 0.04} width={1.2} height={h * 0.92} rx={0.4} fill="#0d0d0d" />
      {/* Rounded caps */}
      <ellipse cx={stripW / 2} cy={2} rx={3} ry={2} fill="#1a1a1a" />
      <ellipse cx={stripW / 2} cy={h - 2} rx={3} ry={2} fill="#1a1a1a" />
      {/* LED */}
      <circle cx={stripW / 2} cy={h * 0.06} r={1} fill="#333" />
    </g>
  );
}

/* ================================================================== */
/*  QR Scanner — mounted flush to the left edge of the tablet.         */
/*  The phone is held in front of it (never inserted), so this is a    */
/*  single static layer — no back/front occlusion split needed.        */
/* ================================================================== */
function QrScannerModule({ x, y, w = 20, h = 56 }) {
  const sW = w;
  return (
    <g transform={`translate(${x},${y})`}>
      <rect width={sW} height={h} rx={4} fill="#1a1a1a" />
      <rect x={1.5} y={2} width={sW - 3} height={h - 4} rx={2.5} fill="#222" />
      {/* Lens */}
      <circle cx={sW / 2} cy={h / 2} r={6} fill="#0a0a0a" stroke="#333" strokeWidth={1} />
      <circle cx={sW / 2 - 1.5} cy={h / 2 - 1.5} r={1.4} fill="#2a2a2a" />
      {/* LED */}
      <circle cx={sW / 2} cy={h * 0.08} r={1} fill="#333" />
    </g>
  );
}

/* ================================================================== */
/*  Scanner glow — lights up green while a QR code is being read       */
/* ================================================================== */
function ScannerGlow({ cx, cy, ledId }) {
  return (
    <g transform={`translate(${cx},${cy})`}>
      <g style={{ animation: `kled-${ledId} ${DUR} ease-in-out infinite` }}>
        <circle r={9} fill="#2ecc71" opacity={0.35} />
        <g style={{
          transformOrigin: "0px 0px",
          animation: `kledscale-${ledId} ${DUR} ease-in-out infinite`,
        }}>
          <circle r={6} fill="#2ecc71" opacity={0.8} />
        </g>
      </g>
    </g>
  );
}

/* ================================================================== */
/*  Scan laser — a green beam sweeping up and down over the QR code    */
/*  while the phone is held in front of the scanner. Gated on the same */
/*  hold window as the scanner glow (kled), so it only runs then; the  */
/*  up/down sweep itself is a fast, independent, always-alternating    */
/*  animation, not tied to the 18s scene timeline.                     */
/* ================================================================== */
function ScanLaser({ x, y, width, ledId }) {
  return (
    <g style={{ animation: `kled-${ledId} ${DUR} ease-in-out infinite` }}>
      <g transform={`translate(${x}, ${y})`}>
        <g style={{ animation: `klsr-${ledId} 1.1s ease-in-out infinite alternate` }}>
          {/* glow */}
          <rect x={0} y={-3} width={width} height={7} fill="#39ff6a" opacity={0.3} />
          {/* beam core */}
          <rect x={0} y={-1} width={width} height={2} fill="#7dffa3" opacity={0.95} />
        </g>
      </g>
    </g>
  );
}

/* ================================================================== */
/*  Keyframes                                                          */
/*                                                                     */
/*  Layout:                                                            */
/*    Tablet: 260x170 at (130, 40). Screen center: (260, 125)         */
/*    Reader back: 36x130 at (374, 60)                                 */
/*    Reader front: 12x130 at (374, 60)                                */
/*    QR scanner: 20x56 at (110, 97), flush to tablet's left edge      */
/*                                                                     */
/*  Card assembly group is placed so that at scale=1.35 + landscape,   */
/*  the card center aligns with screen center (260, 125).              */
/*  At scale=0.55 + portrait, card left edge at ~380.                  */
/*                                                                     */
/*  Phone assembly group is likewise centered on screen at rest; its   */
/*  approach animation is a plain `translate(dx,dy) scale(s)` on an    */
/*  inner group (transform-origin at the phone's own center), so dx/dy */
/*  are literal target screen-pixel offsets independent of scale.      */
/*                                                                     */
/*  Zoom: scale(1.5) centered on reader area (~393, 125).             */
/*    translate = (viewCenterX/s - focusX, viewCenterY/s - focusY)     */
/*             = (260/1.5 - 393, 150/1.5 - 125)                       */
/*             = (173 - 393, 100 - 125) = (-220, -25)                  */
/*  The QR-scanner zoom mirrors this horizontally about the scene's    */
/*  vertical centerline (x=260): focus 112 → translate(41px, -37px).   */
/* ================================================================== */
// Card-only and QR-only modes replay just one act of the dual loop above,
// looped on its own — each act's original breakpoints (as a fraction of
// its own half of the 18s timeline) are doubled onto a full 0-100% range
// running on a halved 9s duration, so every hold/transition keeps the
// exact same real-world timing as it has inside the dual-act loop.
const makeStyles = (id, mode) => {
  if (mode === "card") {
    return css`
      @keyframes kz-${id} {
        0%    { transform: scale(1) translate(0px, 0px); }
        26%   { transform: scale(1) translate(0px, 0px); }
        40%   { transform: scale(1.7) translate(-255px, -37px); }
        66%   { transform: scale(1.7) translate(-255px, -37px); }
        76%   { transform: scale(1.7) translate(-255px, -37px); }
        88%   { transform: scale(1) translate(0px, 0px); }
        100%  { transform: scale(1) translate(0px, 0px); }
      }
      @keyframes ks-${id} {
        0%    { transform: translate(0px, 0px); }
        22%   { transform: translate(0px, 0px); }
        38%   { transform: translate(145px, -75px); }
        76%   { transform: translate(145px, -75px); }
        88%   { transform: translate(0px, 0px); }
        100%  { transform: translate(0px, 0px); }
      }
      @keyframes ksy-${id} {
        0%    { transform: translateY(0px); }
        42%   { transform: translateY(0px); }
        58%   { transform: translateY(160px); }
        76%   { transform: translateY(160px); }
        88%   { transform: translateY(0px); }
        100%  { transform: translateY(0px); }
      }
      @keyframes ksc-${id} {
        0%    { transform: scale(${SCALE_START}); }
        22%   { transform: scale(${SCALE_START}); }
        38%   { transform: scale(${SCALE_END}); }
        76%   { transform: scale(${SCALE_END}); }
        88%   { transform: scale(${SCALE_START}); }
        100%  { transform: scale(${SCALE_START}); }
      }
      @keyframes kr-${id} {
        0%      { transform: rotate(0deg); }
        22%     { transform: rotate(0deg); }
        38%     { transform: rotate(90deg); }
        87.99%  { transform: rotate(90deg); }
        88%     { transform: rotate(0deg); }
        100%    { transform: rotate(0deg); }
      }
      @keyframes kf-${id} {
        0%    { transform: scaleX(1); }
        22%   { transform: scaleX(1); }
        32%   { transform: scaleX(0); }
        38%   { transform: scaleX(1); }
        100%  { transform: scaleX(1); }
      }
      @keyframes ko-${id} {
        0%    { opacity: 1; }
        60%   { opacity: 1; }
        68%   { opacity: 1; }
        76%   { opacity: 0; }
        100%  { opacity: 0; }
      }
      @keyframes kff-${id} {
        0%      { opacity: 1; }
        31.99%  { opacity: 1; }
        32%     { opacity: 0; }
        87.99%  { opacity: 0; }
        88%     { opacity: 1; }
        100%    { opacity: 1; }
      }
      @keyframes krf-${id} {
        0%      { opacity: 0; }
        37.99%  { opacity: 0; }
        38%     { opacity: 1; }
        76%     { opacity: 1; }
        78%     { opacity: 0; }
        100%    { opacity: 0; }
      }
      @keyframes kbf-${id} {
        0%      { opacity: 0; }
        31.99%  { opacity: 0; }
        32%     { opacity: 1; }
        87.99%  { opacity: 1; }
        88%     { opacity: 0; }
        100%    { opacity: 0; }
      }
    `;
  }
  if (mode === "qr") {
    return css`
      @keyframes kz-${id} {
        0%    { transform: scale(1) translate(0px, 0px); }
        26%   { transform: scale(1) translate(0px, 0px); }
        40%   { transform: scale(1.7) translate(41px, -37px); }
        66%   { transform: scale(1.7) translate(41px, -37px); }
        72%   { transform: scale(1.7) translate(41px, -37px); }
        84%   { transform: scale(1) translate(0px, 0px); }
        100%  { transform: scale(1) translate(0px, 0px); }
      }
      @keyframes kpo-${id} {
        0%    { opacity: 0; }
        6%    { opacity: 1; }
        72%   { opacity: 1; }
        80%   { opacity: 0; }
        100%  { opacity: 0; }
      }
      @keyframes kpt-${id} {
        0%    { transform: translate(0px, 0px) scale(${PHONE_SCALE_START}); }
        6%    { transform: translate(0px, 0px) scale(${PHONE_SCALE_START}); }
        22%   { transform: translate(0px, 0px) scale(${PHONE_SCALE_START}); }
        38%   { transform: translate(-170px, 0px) scale(${PHONE_SCALE_END}); }
        44%   { transform: translate(-170px, 0px) scale(${PHONE_SCALE_END}); }
        72%   { transform: translate(-170px, 0px) scale(${PHONE_SCALE_END}); }
        86%   { transform: translate(0px, 0px) scale(${PHONE_SCALE_START}); }
        100%  { transform: translate(0px, 0px) scale(${PHONE_SCALE_START}); }
      }
      @keyframes kled-${id} {
        0%    { opacity: 0; }
        40%   { opacity: 0; }
        44%   { opacity: 1; }
        70%   { opacity: 1; }
        74%   { opacity: 0; }
        100%  { opacity: 0; }
      }
      @keyframes kledscale-${id} {
        0%    { transform: scale(1); }
        40%   { transform: scale(1); }
        60%   { transform: scale(1); }
        66%   { transform: scale(1.6); }
        72%   { transform: scale(1); }
        100%  { transform: scale(1); }
      }
      @keyframes klsr-${id} {
        0%    { transform: translateY(0px); }
        100%  { transform: translateY(${LASER_SWEEP}px); }
      }
    `;
  }
  return css`
  /* Scene zoom — pulled back to 1.7x for more context around reader/scanner
     Card reader focus (408,125):  tx = 260/1.7 - 408 = -255, ty = -37
     QR scanner focus (112,125):   tx = 260/1.7 - 112 =   41, ty = -37 */
  @keyframes kz-${id} {
    0%    { transform: scale(1) translate(0px, 0px); }
    13%   { transform: scale(1) translate(0px, 0px); }
    20%   { transform: scale(1.7) translate(-255px, -37px); }
    33%   { transform: scale(1.7) translate(-255px, -37px); }
    38%   { transform: scale(1.7) translate(-255px, -37px); }
    44%   { transform: scale(1) translate(0px, 0px); }
    50%   { transform: scale(1) translate(0px, 0px); }
    63%   { transform: scale(1) translate(0px, 0px); }
    70%   { transform: scale(1.7) translate(41px, -37px); }
    83%   { transform: scale(1.7) translate(41px, -37px); }
    86%   { transform: scale(1.7) translate(41px, -37px); }
    92%   { transform: scale(1) translate(0px, 0px); }
    100%  { transform: scale(1) translate(0px, 0px); }
  }

  /* Card approach + reset (ease-in-out) — moves to reader position, Y at top of slot */
  @keyframes ks-${id} {
    0%    { transform: translate(0px, 0px); }
    11%   { transform: translate(0px, 0px); }
    19%   { transform: translate(145px, -75px); }
    38%   { transform: translate(145px, -75px); }
    44%   { transform: translate(0px, 0px); }
    50%   { transform: translate(0px, 0px); }
    100%  { transform: translate(0px, 0px); }
  }

  /* Card swipe Y only (aggressive bell curve) — separate from approach */
  @keyframes ksy-${id} {
    0%    { transform: translateY(0px); }
    21%   { transform: translateY(0px); }
    29%   { transform: translateY(160px); }
    38%   { transform: translateY(160px); }
    44%   { transform: translateY(0px); }
    50%   { transform: translateY(0px); }
    100%  { transform: translateY(0px); }
  }

  /* Card scale: large → small → large */
  @keyframes ksc-${id} {
    0%    { transform: scale(${SCALE_START}); }
    11%   { transform: scale(${SCALE_START}); }
    19%   { transform: scale(${SCALE_END}); }
    38%   { transform: scale(${SCALE_END}); }
    44%   { transform: scale(${SCALE_START}); }
    50%   { transform: scale(${SCALE_START}); }
    100%  { transform: scale(${SCALE_START}); }
  }

  /* Card rotation: landscape → portrait */
  @keyframes kr-${id} {
    0%      { transform: rotate(0deg); }
    11%     { transform: rotate(0deg); }
    19%     { transform: rotate(90deg); }
    43.995% { transform: rotate(90deg); }
    44%     { transform: rotate(0deg); }
    50%     { transform: rotate(0deg); }
    100%    { transform: rotate(0deg); }
  }

  /* Card flip — pushed later so back face reveals closer to arrival */
  @keyframes kf-${id} {
    0%      { transform: scaleX(1); }
    11%     { transform: scaleX(1); }
    16%     { transform: scaleX(0); }
    19%     { transform: scaleX(1); }
    50%     { transform: scaleX(1); }
    100%    { transform: scaleX(1); }
  }

  /* Card opacity — visible through Act 1, hidden through all of Act 2,
     fades back in right at the seam so Act 1 can repeat */
  @keyframes ko-${id} {
    0%    { opacity: 1; }
    30%   { opacity: 1; }
    34%   { opacity: 1; }
    38%   { opacity: 0; }
    96%   { opacity: 0; }
    100%  { opacity: 1; }
  }

  /* Front face visibility — shifted to match flip at 16% */
  @keyframes kff-${id} {
    0%       { opacity: 1; }
    15.995%  { opacity: 1; }
    16%      { opacity: 0; }
    43.995%  { opacity: 0; }
    44%      { opacity: 1; }
    50%      { opacity: 1; }
    100%     { opacity: 1; }
  }

  /* Tablet occluder — hidden while card is in front, snaps on when card reaches reader */
  @keyframes krf-${id} {
    0%       { opacity: 0; }
    18.995%  { opacity: 0; }
    19%      { opacity: 1; }
    38%      { opacity: 1; }
    39%      { opacity: 0; }
    50%      { opacity: 0; }
    100%     { opacity: 0; }
  }

  /* Back face visibility — shifted to match flip at 16% */
  @keyframes kbf-${id} {
    0%       { opacity: 0; }
    15.995%  { opacity: 0; }
    16%      { opacity: 1; }
    43.995%  { opacity: 1; }
    44%      { opacity: 0; }
    50%      { opacity: 0; }
    100%     { opacity: 0; }
  }

  /* Phone opacity — hidden through Act 1, visible through the hold/scan
     window of Act 2 */
  @keyframes kpo-${id} {
    0%    { opacity: 0; }
    50%   { opacity: 0; }
    53%   { opacity: 1; }
    86%   { opacity: 1; }
    90%   { opacity: 0; }
    100%  { opacity: 0; }
  }

  /* Phone approach — plain translate+scale (no rotate/flip; the phone   */
  /* is held up to the scanner, not inserted through it) */
  @keyframes kpt-${id} {
    0%    { transform: translate(0px, 0px) scale(${PHONE_SCALE_START}); }
    50%   { transform: translate(0px, 0px) scale(${PHONE_SCALE_START}); }
    53%   { transform: translate(0px, 0px) scale(${PHONE_SCALE_START}); }
    61%   { transform: translate(0px, 0px) scale(${PHONE_SCALE_START}); }
    69%   { transform: translate(-170px, 0px) scale(${PHONE_SCALE_END}); }
    72%   { transform: translate(-170px, 0px) scale(${PHONE_SCALE_END}); }
    86%   { transform: translate(-170px, 0px) scale(${PHONE_SCALE_END}); }
    93%   { transform: translate(0px, 0px) scale(${PHONE_SCALE_START}); }
    100%  { transform: translate(0px, 0px) scale(${PHONE_SCALE_START}); }
  }

  /* Scanner glow — lights up once the phone settles in front of it */
  @keyframes kled-${id} {
    0%    { opacity: 0; }
    70%   { opacity: 0; }
    72%   { opacity: 1; }
    85%   { opacity: 1; }
    87%   { opacity: 0; }
    100%  { opacity: 0; }
  }

  /* Scanner glow pulse — a brief "success" blip near the end of the hold */
  @keyframes kledscale-${id} {
    0%    { transform: scale(1); }
    70%   { transform: scale(1); }
    80%   { transform: scale(1); }
    83%   { transform: scale(1.6); }
    86%   { transform: scale(1); }
    100%  { transform: scale(1); }
  }

  /* Scan laser sweep — fast, independent up/down bounce (opacity-gated
     by kled above, not by this keyframe) */
  @keyframes klsr-${id} {
    0%    { transform: translateY(0px); }
    100%  { transform: translateY(${LASER_SWEEP}px); }
  }
`;
};

/* ================================================================== */
/*  Exported component                                                 */
/* ================================================================== */
export default React.memo(function KioskSwipeInstructionSVG({
  animate = true,
  mode = "both", // "both" | "card" | "qr" — which act(s) of the loop to show
  width = "100%",
  maxWidth = "520px",
}) {
  const id = useMemo(() => Math.random().toString(36).slice(2, 7), []);
  const styles = useMemo(() => makeStyles(id, mode), [id, mode]);
  const showCard = mode !== "qr";
  const showPhone = mode !== "card";
  // In the static (non-animated) frame, feature whichever actor is actually
  // enabled — a card-less kiosk (mode="qr") has nothing to freeze-frame on
  // the card side, so show the phone instead.
  const staticActorIsPhone = mode === "qr";
  const dur = mode === "both" ? "18s" : "9s";

  const viewW = 520;
  const viewH = 300;

  // Tablet: centered
  const tabW = 260, tabH = 170;
  const tabX = (viewW - tabW) / 2; // 130
  const tabY = 40;

  // Reader: right edge of tablet, vertically centered
  const rdrH = 56;
  const rdrX = tabX + tabW; // flush with tablet right edge (390)
  const rdrY = tabY + (tabH - rdrH) / 2; // 60

  // QR scanner: left edge of tablet, vertically centered (mirrors the reader)
  const scnW = 20;
  const scnH = 56;
  const scnX = tabX - scnW; // flush with tablet left edge (110)
  const scnY = tabY + (tabH - scnH) / 2; // 97

  // Card group start: positioned so card center = tablet screen center
  // Screen center = (tabX + tabW/2, tabY + tabH/2) = (260, 125)
  // At SCALE_START, the card center in scaled coords = (CCX * SCALE_START, CCY * SCALE_START)
  // But scale is applied as a separate animation, so group translate puts the
  // unscaled card center at the screen center.
  // Card center in group-local = (CCX, CCY) = (90, 56.5)
  // Group translate = (260 - 90, 125 - 56.5) = (170, 68.5)
  const cardGroupX = 170;
  const cardGroupY = 68.5;

  // Phone group start: same idea, phone center = tablet screen center
  // Phone center in group-local = (PCX, PCY) = (32, 64)
  // Group translate = (260 - 32, 125 - 64) = (228, 61)
  const phoneGroupX = 228;
  const phoneGroupY = 61;

  const ease = `${dur} ease-in-out infinite`;
  const step = `${dur} steps(1, end) infinite`;

  const ariaLabel =
    mode === "card"
      ? "Swipe your UMD ID card through the card reader on the right side of the tablet"
      : mode === "qr"
      ? "Hold your phone's QR code up to the scanner on the left side of the tablet"
      : "Swipe your UMD ID card through the card reader on the right side of the tablet, or hold your phone's QR code up to the scanner on the left side";

  return (
    <>
      {animate && <Global styles={styles} />}
      <svg
        viewBox={`0 0 ${viewW} ${viewH}`}
        width={width}
        style={{ maxWidth }}
        xmlns="http://www.w3.org/2000/svg"
        role="img"
        aria-label={ariaLabel}
      >
        {/* Scene group — zoom target */}
        <g style={{
          transformOrigin: "0px 0px",
          ...(animate ? { animation: `kz-${id} ${ease}` } : {}),
        }}>

          {/* Layer 1: Tablet on stand */}
          <TabletOnStand x={tabX} y={tabY} w={tabW} h={tabH} uid={id} />

          {/* Layer 2: Reader back (behind card) + QR scanner module */}
          <ReaderBack x={rdrX} y={rdrY} h={rdrH} />
          <QrScannerModule x={scnX} y={scnY} w={scnW} h={scnH} />
          {showPhone && <ScannerGlow cx={scnX + scnW / 2} cy={scnY + scnH / 2} ledId={id} />}

          {/* Layer 3: Card assembly (in front of tablet) */}
          <g
            style={
              !showCard
                ? { opacity: 0 }
                : animate ? { animation: `ko-${id} ${ease}` } : undefined
            }
          >
            <g style={showCard && animate ? {
              animation: `ks-${id} ${ease}`,
            } : undefined}>
            <g style={showCard && animate ? {
              animation: `ksy-${id} ${dur} cubic-bezier(0.76, 0, 0.24, 1) infinite`,
            } : undefined}>
            <g transform={`translate(${cardGroupX}, ${cardGroupY})`}>
              <g style={{
                transformOrigin: `${CCX}px ${CCY}px`,
                ...(showCard && animate ? { animation: `ksc-${id} ${ease}` } : {
                  transform: `scale(${SCALE_START})`,
                }),
              }}>
                <g style={{
                  transformOrigin: `${CCX}px ${CCY}px`,
                  ...(showCard && animate ? { animation: `kr-${id} ${ease}` } : {}),
                }}>
                  <g style={{
                    transformOrigin: `${CCX}px ${CCY}px`,
                    ...(showCard && animate ? { animation: `kf-${id} ${ease}` } : {}),
                  }}>
                    <g style={showCard && animate ? { animation: `kff-${id} ${step}` } : undefined}>
                      <CardFront uid={id} />
                    </g>
                    <g style={showCard && animate ? { animation: `kbf-${id} ${step}` } : { opacity: 0 }}>
                      <g transform={`rotate(180, ${CCX}, ${CCY})`}>
                        <CardBack />
                      </g>
                    </g>
                  </g>
                </g>
              </g>
            </g>
          </g>
          </g>
          </g>

          {/* Layer 4: Tablet + reader occluder (on top of card — fades in when card reaches reader) */}
          <g style={showCard && animate ? { animation: `krf-${id} ${ease}` } : { opacity: 0 }}>
            <TabletOnStand x={tabX} y={tabY} w={tabW} h={tabH} uid={`${id}-occ`} />
            <ReaderFront x={rdrX} y={rdrY} h={rdrH} />
          </g>

          {/* Layer 5: Phone assembly (always in front — never inserted/occluded) */}
          <g style={
            !showPhone
              ? { opacity: 0 }
              : animate
              ? { opacity: 0, animation: `kpo-${id} ${ease}` }
              : { opacity: staticActorIsPhone ? 1 : 0 }
          }>
            <g transform={`translate(${phoneGroupX}, ${phoneGroupY})`}>
              <g style={{
                transformOrigin: `${PCX}px ${PCY}px`,
                ...(showPhone && animate ? { animation: `kpt-${id} ${ease}` } : {
                  transform: `scale(${PHONE_SCALE_START})`,
                }),
              }}>
                <PhoneWithQr uid={id} />
                {animate && showPhone && (
                  <ScanLaser x={QR_LEFT} y={QR_TOP} width={QR_WIDTH} ledId={id} />
                )}
              </g>
            </g>
          </g>

        </g>
      </svg>
    </>
  );
});
